import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

declare const grecaptcha: { ready: (fn: () => void) => void; execute: (key: string, opts: { action: string }) => Promise<string>; };

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private siteKey: string = ''; // Will be set from environment
  private scriptLoaded = false;

  constructor() {}

  /**
   * Initialize reCAPTCHA with the site key
   * @param siteKey Your Google reCAPTCHA v3 site key
   */
  init(siteKey: string, lang: string = 'en'): void {
    this.siteKey = siteKey;
    if (!this.scriptLoaded) {
      this.loadScript(lang);
    }
  }

  private loadScript(lang: string = 'en'): void {
    if (typeof grecaptcha !== 'undefined') {
      this.scriptLoaded = true;
      return;
    }

    // Check if script already exists in DOM (loaded from index.html)
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      this.scriptLoaded = true;
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}&hl=${lang}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      this.scriptLoaded = true;
    };

    script.onerror = (error) => {
      console.error('reCAPTCHA script failed to load via dynamic injection:', error);
    };

    document.head.appendChild(script);
  }

  /**
   * Execute reCAPTCHA and get token
   * @param action The action name for this reCAPTCHA execution
   * @returns Observable with the reCAPTCHA token
   */
  execute(action: string): Observable<string> {
    return new Observable((observer) => {
      // Wait for grecaptcha to be available
      const maxRetries = 100; // Wait up to 10 seconds
      let retries = 0;

      const checkAndExecute = () => {
        // Check window object for loading status
        const windowAny = window as Window & { grecaptchaLoaded?: boolean };

        // Check if grecaptcha is defined and has the ready method
        if (typeof grecaptcha !== 'undefined' && grecaptcha && typeof grecaptcha.ready === 'function') {
          try {
            grecaptcha.ready(() => {
              try {
                grecaptcha.execute(this.siteKey, { action }).then(
                  (token: string) => {
                    observer.next(token);
                    observer.complete();
                  },
                  ( error: any) => {
                    console.error('reCAPTCHA execute error:', error);
                    observer.error(error);
                  }
                );
              } catch (executeError) {
                console.error('reCAPTCHA ready callback error:', executeError);
                observer.error(executeError);
              }
            });
          } catch (readyError) {
            console.error('reCAPTCHA ready error:', readyError);
            if (retries < maxRetries) {
              retries++;
              setTimeout(checkAndExecute, 100);
            } else {
              observer.error('reCAPTCHA not loaded');
            }
          }
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(checkAndExecute, 100);
        } else {
          console.error('reCAPTCHA timeout - grecaptcha object:', typeof grecaptcha);
          console.error('reCAPTCHA script element:', document.querySelector('script[src*="recaptcha"]'));
          console.error('Window grecaptchaLoaded flag:', windowAny.grecaptchaLoaded);
          observer.error('reCAPTCHA not loaded');
        }
      };

      checkAndExecute();
    });
  }

  /**
   * Check if reCAPTCHA is loaded and ready
   */
  isReady(): boolean {
    return this.scriptLoaded && typeof grecaptcha !== 'undefined';
  }
}
